import { ReactElement, SyntheticEvent } from 'react';
import { useDispatch } from 'react-redux';

import type { Merchandising } from '@/core/tamagoshi/ProductCard/interfaces';
import { SafeStorage } from '@/domains/core/data/utils/SafeStorage/SafeStorage';
import { isB2B, isGermanyPlatform } from '@/domains/core/settings/utils';
import { useTrackViewedElement } from '@/domains/core/tracking/hooks/useTrackViewedElement';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { getTaxKey } from '@/domains/productDiscovery/algolia/productProcessor';
import { calculateCouponAmount } from '@/domains/productDiscovery/CommercialAnimation/utils/calculateCouponAmount';
import { getDelivery } from '@/domains/productDiscovery/ProductCard/utils/getDelivery';
import type { AddToCartPayload } from '@/domains/shoppingCart/AddToCart/AddToCart.actions';
import type { Product as ProductCart } from '@/domains/shoppingCart/AddToCart/AddToCart.interfaces';
import { CROSS_SELL_A2C_PRODUCTS_KEY } from '@/domains/shoppingCart/CartPage/components/CartByDispatches/contants';
import { displayAdvertiserInformation } from '@/productDiscovery/CommercialAnimation/CommercialAnimation.actions';
import { createAdvertiserInformationPopupContent } from '@/productDiscovery/CommercialAnimation/utils/createAdvertiserInformationPopupContent';
import { getSecondHandInfo } from '@/sustainability/secondHand/utils/secondHand';

import {
  ProductCard,
  type ProductCardProps,
} from '../../../ProductCard/ProductCard';
import type { Product, ProductTitleKey } from '../../interfaces/product';
import {
  generateUrl,
  mapTaxToProductCardTax,
} from './AlgoliaProductCard.utils';
import { useAddToCartState } from './hooks/useAddToCartState';
import type { ProductCardClickTracking } from './interfaces/clickTracking';

export type AlgoliaProductCardProps<P extends Product = Product> = Omit<
  ProductCardProps,
  | 'url'
  | 'onClick'
  | 'title'
  | 'price'
  | 'imageSrc'
  | 'onAddToCart'
  | 'couponAmount'
> & {
  className?: string;
  provideImageDataSrc?: boolean;
  product: P;
  customTitleFormatting?: boolean;
  addToCart?: ProductCardProps['addToCart'];
  onAddToCart?: (payload: AddToCartPayload) => void;
  // Used for tracking purpose
  onClick?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    product: P,
    position: number,
    defaultTracking: ProductCardClickTracking,
  ) => void;
  position: number;
  trackingListName?: string;
  /**
   * Enable intersection observer to track if the product card is viewed.
   * @param product is the full product object
   */
  onViewed?(product: P): void;
  displayAddToCartCustomSummary?: boolean;
  displayAddToCartModal?: boolean;
  autoUpdateAddToCartStateDisabled?: boolean;
  enablePropagationOnAddToCart?: boolean;
  multipleImages?: boolean;
  promoted?: boolean;
  favourite?: boolean;
  adPayer?: 'BRAND' | 'SELLER';
  productIsSponsored?: boolean;
};

export const AlgoliaProductCard = <P extends Product = Product>({
  product,
  b2b,
  position,
  trackingListName,
  customTitleFormatting,
  displayDeliveryLabel,
  onClick,
  promoted,
  onViewed,
  displayAddToCartCustomSummary = false,
  displayAddToCartModal = false,
  autoUpdateAddToCartStateDisabled = false,
  enablePropagationOnAddToCart = false,
  onAddToCart,
  merchandising,
  multipleImages = false,
  adPayer,
  productIsSponsored,
  ...otherProps
}: AlgoliaProductCardProps<P>): ReactElement<any, any> | null => {
  const addToCartState = useAddToCartState(product);

  const productMerchandising: Merchandising[] | undefined = (() => {
    if (merchandising && merchandising.length) return merchandising;
    if (product.coupon) {
      return ['coupon'];
    }
    if (product.hasPromoLabel) {
      return ['promo'];
    }
  })();

  const isSponsored =
    productMerchandising?.includes('sponsored') || productIsSponsored;

  const [ref] = useTrackViewedElement(
    () => {
      if (onViewed) {
        onViewed(product);
      }
    },
    { skip: !onViewed },
  );

  const trackProductCard = async (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    const { computeDefaultTrackingData } = await import('./tracking');
    const trackingData = computeDefaultTrackingData({
      listName: trackingListName,
      position,
      product,
      price: product.prices.main,
      isSponsored,
    });

    if (onClick) {
      onClick(event, product, position, trackingData);
    } else {
      Gtm.push(trackingData);
    }
  };

  const dispatch = useDispatch();
  const handleAddToCart = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    const productId = parseInt(product.productId, 10);
    if (!enablePropagationOnAddToCart) {
      event.stopPropagation();

      const safeStorage = SafeStorage.getLocalStorage();
      const savedProductsIds = safeStorage.getItem(CROSS_SELL_A2C_PRODUCTS_KEY);

      safeStorage.setItem(
        CROSS_SELL_A2C_PRODUCTS_KEY,
        savedProductsIds ? `${savedProductsIds},${productId}` : productId,
      );
    }

    const productCart: ProductCart = {
      articleId: product.articleId,
      name: product.title,
      picture: product.imageFullpath || '',
      id: productId,
      quantity: 1,
      stockSize: 1, // TODO: À voir ou récupérer l'info
      sellerName: product.sellerName,
      brandName: product.brandName,
      isDeliveredByManomano: product.isMmf,
      b2b: product.isSellerB2b,
      unitPrice: parseInt('', 10), // because it was already null value
      reference: product.productId,
      oldUnitPrice: parseInt('', 10), // because it was already null value
      prices: product.prices,
      pr_model_id: product.modelId,
      pr_is_sponsored: productIsSponsored,
    };

    if (onAddToCart) {
      if (isB2B()) {
        event.stopPropagation();
      }
      onAddToCart({
        products: [productCart],
        displayAddToCartCustomSummary,
      });
    } else {
      const { addToCart } = await import(
        '@/domains/shoppingCart/AddToCart/AddToCart.actions'
      );
      dispatch(
        addToCart({
          products: [productCart],
          displayAddToCartCustomSummary,
        }),
      );
    }
  };

  const titleField: ProductTitleKey =
    promoted || b2b ? 'title' : 'defaultTitle';

  const title =
    customTitleFormatting && product.highlightResult
      ? product.highlightResult[titleField].value
      : product[titleField];

  const retailPrice = !product.prices.retail
    ? undefined
    : product.prices.retail;
  const { isSecondHand } = getSecondHandInfo(product.title);
  const onAdvertiserLabelClicked = async (e: SyntheticEvent) => {
    if (adPayer) {
      e.preventDefault();
      const sponsor =
        adPayer === 'SELLER' ? product.sellerName : product.brandName;
      const adInfo = {
        advertiser: product.sellerName,
        sponsor,
        userSegments: true,
        dsaPopup: !!(product.sellerName || product.sellerName),
        content: createAdvertiserInformationPopupContent(
          product.sellerName,
          sponsor,
          true,
        ),
      };
      dispatch(displayAdvertiserInformation(adInfo));
    }
  };

  return (
    <ProductCard
      {...otherProps}
      ref={ref}
      title={title}
      price={product.prices.main}
      secondaryPrice={product.prices.secondary || undefined}
      retailPrice={retailPrice}
      imageSrc={product.imageFullpath || ''}
      brandImageSrc={product.brandImageFullpath}
      brandName={product.brandName}
      modelsCount={product.modelsCount}
      rating={product.rating}
      ratingCount={product.ratingCount}
      sellerName={product.sellerName}
      adPayer={adPayer}
      url={generateUrl(product, promoted, isSponsored)}
      pro={product.hasProConditions}
      delivery={getDelivery(product)}
      b2b={b2b}
      onClick={trackProductCard}
      onAddToCart={handleAddToCart}
      customTitleFormatting={customTitleFormatting}
      displayDeliveryLabel={displayDeliveryLabel}
      madeIn={product.isLocal ? 'fr' : undefined}
      ecoResponsible={!!product.isEcoResponsible}
      addToCartState={
        autoUpdateAddToCartStateDisabled ? 'available' : addToCartState
      }
      tax={b2b ? mapTaxToProductCardTax(getTaxKey()) : undefined}
      couponAmount={calculateCouponAmount(product.coupon)}
      secondaryUnit={product.prices.unit || undefined}
      multipleImages={multipleImages}
      legalMention={isGermanyPlatform()}
      legalMentionNoVAT={product.prices.hasNoVATForDE}
      merchandising={productMerchandising}
      secondHand={isSecondHand}
      addToCartButtonVariant="primary"
      onAdvertiserLabelClicked={onAdvertiserLabelClicked}
    />
  );
};

AlgoliaProductCard.displayName = 'AlgoliaProductCard';
